import {axiosDeleteHelper} from '../../shared/service/axios-delete-helper';
import {AxiosDeletePayload} from '../../shared/service/axios-delete-payload';
import {axiosReadHelper} from '../../shared/service/axios-read-helper';
import {AxiosReadPayload} from '../../shared/service/axios-read-payload';
import {
  AxiosResultDeleteStatus,
} from '../../shared/service/axios-result-delete-status';
import {AxiosResultStatus} from '../../shared/service/axios-result-status';
import {axiosSaveHelper} from '../../shared/service/axios-save-helper';
import {AxiosSavePayload} from '../../shared/service/axios-save-payload';
import {
  getPatientStatusByStatusId,
  StatusType,
  WorkflowId,
} from '../components/misc/patient-status';
import {PFRAdjustment} from '../components/models/pfr-adjustment';
import {
  InstanceOfCareViewModel,
} from '../components/models/instance-of-care-view-model';
import {
  PatientEncounter, patientEncounterContact,
} from '../components/models/patient-encounter';
import {
  PatientEncounterCard,
} from '../components/models/patient-encounter-card';
import {
  PatientPaymentProgram,
} from '../components/models/patient-payment-program';
import {Payment, PaymentReversal} from '../components/models/payment';
import {
  authDocDispatchMethod,
  StatusColors,
} from '../../shared/enums';
import {
  PaymentMethod,
  PaymentIntents
} from '../components/models/payment-method';
import {PaymentDetail} from '../components/models/payment-detail';
import {StripePaymentMethod} from '../components/models/stripe-payment-method';
import {Transaction} from '../components/models/transaction';
import {showErrorStatus} from '../../security/state/user-slice';
import {
  ACHVerification,
} from '../../account-holder/models/verify-ach-payment-method';
import {axiosDownloadHelper} from '../../shared/service/axios-download-helper';
import {
  axiosDownloadFileHelper,
} from '../../shared/service/axios-download-file-helper';
import _ from 'lodash';
import {
  FormattedDocument,
  PatientDocument,
  SortedDocuments,
  UnFormattedDocument,
} from '../components/models/patient-document';
import {Utils} from '../../shared/utils';
import {SignatureStatus} from '../components/models/signature-tracking';
import {Workflow} from '../../shared/model/client';
import { V2PaymentDetail } from 'src/patient/components/models/payment';

export const patientHelper = {

  filterConvertedPatients: (patients: PatientEncounterCard[]) => {
    patients = patientHelper.setStatusColor(patients, WorkflowId.Converted);

    return {
      goodStanding: patients.filter(
        (patient) =>
          patient.workflow?.workflowStatus?.workflowStatusId === 8 ||
          patient.workflow?.workflowStatus?.workflowStatusId === 12
      ),
      reminders: patients.filter((patient) => patient.hasReminder),
      paid: patients.filter(
        (patient) => patient.workflow?.workflowStatus?.workflowStatusId === 16
      ),

      // priority
      awaitingAuthorizations: patientHelper.priorityFilter(
        patients,
        "Awaiting Authorizations"
      ),
      awaitingACHVerification: patientHelper.priorityFilter(
        patients,
        "Awaiting ACH Verification"
      ),

      // at risk
      atRisk: patients.filter(
        (patient) => patient.workflow?.workflowStatus?.workflowStatusId === 10
      ), // used only for filter 'count'
      missedPayment: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 25
      ),
      pastDueBalance: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 24
      ),
      onHold: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 26
      ),
      balanceAdjustmentRequired: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 36
      ),
      missingFundingSource: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 59
      ),

      // closed
      closed: patients.filter(
        (patient) => patient.workflow?.workflowStatus?.workflowStatusId === 7
      ), // used only for filter 'count'
      fileError_converted: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 27
      ),
      patientFailedToEngage_converted: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 28
      ),
      patientNeverAdmitted_converted: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 29
      ),
      default: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 37
      ),
      finpayFailedToEngage: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 48
      ),
      duplicateRecord: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 49
      ),
      insurancePaidInFull: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 50
      ),
      accountHolderPaidBalanceOwed: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 51
      ),
      accountHolderPaidBalanceOwedDueARefund: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 52
      ),
      revokedPaymentPlan: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 53
      ),
      providersRequest: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 54
      ),
      refinanced: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 55
      ),
      hreApprovedDirectBillPaymentMadeToFacility: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 56
      ),
      hreApprovedPaymentAtFacility: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 57
      ),
      hreApprovedFullScholarship: patients.filter(
        (patient) =>
          patient.workflow?.workflowSubStatus?.workflowSubStatusId === 58
      ),
    };
  },
  setStatusColor: (patients: PatientEncounterCard[], workflow: WorkflowId) => {
    return patients.map((patientCard) => {
      // Status: NEW
      if (
        patientCard?.workflow?.workflowStatus?.workflowStatusId === null ||
        patientCard?.workflow?.workflowStatus?.workflowStatusId === 13
      ) {
        return {
          ...patientCard,
          cardColor: StatusColors.blue,
        };
      } else {
        return {
          ...patientCard,
          cardColor:
            patientCard?.workflow?.workflowSubStatus?.workflowSubStatusDesc ===
            "None"
              ? getPatientStatusByStatusId(
                  patientCard?.workflow?.workflowStatus?.workflowStatusId,
                  StatusType["primary-status"],
                  workflow
                )?.color
              : getPatientStatusByStatusId(
                  patientCard?.workflow?.workflowSubStatus?.workflowSubStatusId,
                  StatusType["sub-status"],
                  workflow
                )?.color,
        };
      }
    });
  },
  priorityFilter: (patients: PatientEncounterCard[], statusDesc: string) => {
    return patients
      .filter(
        (patient) =>
          patient.workflow.workflowSubStatus?.workflowSubStatusId === 30 || patient.workflow.workflowSubStatus?.workflowSubStatusId === 34
      )
      .filter((patient) => patient?.finClearanceStatus?.includes(statusDesc));
  },

  consolidateTransactions: (
    transactions: Transaction[],
    isAccountHolder: boolean
  ) => {
    const refundedPaymentIds =
      transactions &&
      transactions?.length > 0 &&
      new Set(
        transactions
          ?.filter(
            (transaction: Transaction) =>
              transaction?.paymentReversal?.paymentId
          )
          .map(
            (transaction: Transaction) => transaction.paymentReversal.paymentId
          )
      );
    let groupedTransactions: Transaction[] = [];
    const processedPaymentIds = new Set<number>(); // Track which payments we've already added
    const processedDisputeIds = new Set<number>(); // Track which disputes we've already added
    
    transactions &&
      transactions?.length > 0 &&
      transactions?.forEach((transaction: Transaction) => {
        // Handle payments
        if (transaction?.payment && transaction?.payment?.paymentId) {
          // Skip $0 payments
          if (transaction?.payment?.paymentAmt === 0) {
            return;
          }
          
          if (!processedPaymentIds.has(transaction.payment.paymentId)) {
            processedPaymentIds.add(transaction.payment.paymentId);
            
            if (
              refundedPaymentIds &&
              refundedPaymentIds.has(transaction?.payment?.paymentId)
            ) {
              // this means that we have a reversal for this transaction.
              // we need to find that payment reversal and then include it in the transaction.
              if (
                isAccountHolder &&
                transaction?.payment?.paymentMethodUsed?.ach &&
                transaction.payment?.isCaptured &&
                transaction?.payment?.paymentStatus !== "Success"
              ) {
                // do not push to the array if the transaction has a refund (it's an ACH captured transaction that is also not successful).
              } else {
                const matchingPaymentReversal = transactions?.find(
                  (searchTransaction: Transaction) =>
                    searchTransaction?.paymentReversal?.paymentId ===
                    transaction?.payment?.paymentId
                );
                groupedTransactions.push({
                  payment: transaction?.payment,
                  paymentReversal:
                    matchingPaymentReversal?.paymentReversal ||
                    ({} as PaymentReversal),
                });
              }
            } else {
              // Show payment without dispute attached (disputes will be separate rows)
              groupedTransactions.push({
                payment: transaction?.payment,
                paymentReversal: {} as PaymentReversal,
              });
            }
          }
        }
        
        // Handle disputes as separate rows - track by paymentDisputeId to show ALL dispute events
        if (transaction?.paymentDispute && 
            transaction?.paymentDispute?.paymentDisputeId &&
            !processedDisputeIds.has(transaction.paymentDispute.paymentDisputeId)) {
          processedDisputeIds.add(transaction.paymentDispute.paymentDisputeId);
          
          // Add each dispute as a separate transaction row
          groupedTransactions.push({
            payment: {} as Payment,
            paymentReversal: {} as PaymentReversal,
            paymentDispute: transaction.paymentDispute,
          });
        }
      });
    
    // Sort transactions chronologically by date (newest first)
    const sortedTransactions = groupedTransactions.sort((a, b) => {
      const getDate = (transaction: Transaction) => {
        if (transaction?.paymentDispute?.disputeDate) {
          return new Date(transaction.paymentDispute.disputeDate).getTime();
        }
        if (transaction?.paymentReversal?.reversalDate) {
          return new Date(transaction.paymentReversal.reversalDate).getTime();
        }
        if (transaction?.payment?.paymentInitDt) {
          return new Date(transaction.payment.paymentInitDt).getTime();
        }
        return 0;
      };
      
      return getDate(b) - getDate(a); // Descending order (newest first)
    });
    
    return sortedTransactions &&
      sortedTransactions?.length > 0 &&
      sortedTransactions[0]
      ? sortedTransactions
      : [];
  },

  checkNonRefundedAchTransactionsOrPaymentMethods: (
    transactions?: Transaction[] | false | undefined,
    paymentMethods?: StripePaymentMethod[] | false | undefined
  ) => {
    const refundedPaymentIds =
      transactions &&
      transactions?.length > 0 &&
      new Set(
        transactions
          ?.filter(
            (transaction: Transaction) =>
              transaction?.paymentReversal?.paymentId
          )
          .map(
            (transaction: Transaction) => transaction.paymentReversal.paymentId
          )
      );
    let doAnyAchTransactionsExist = false;
    let doAnyAchPaymentMethodsExist = false;

    transactions &&
      transactions?.length > 0 &&
      transactions?.forEach((transaction: Transaction) => {
        // when we return false, we exit the loop.
        if (
          transaction?.payment &&
          transaction?.payment?.paymentMethodUsed?.ach
        ) {
          // we found a transaction that is ACH
          if (
            refundedPaymentIds &&
            refundedPaymentIds.has(transaction?.payment?.paymentId)
          ) {
            // this transaction has a refund, so we don't care about it. (it is canceled out by the refund.)
          } else {
            // there is no refund. this transaction is ACH so we need to exit the loop.
            doAnyAchTransactionsExist = true;
          }
        }
      });

    paymentMethods &&
      paymentMethods?.length > 0 &&
      paymentMethods?.forEach((paymentMethod: StripePaymentMethod) => {
        if (
          (paymentMethod?.metadata?.metaData_paymentMethodType?.includes(
            "RECURRING"
          ) ||
            paymentMethod?.metadata?.isRecurring) &&
          paymentMethod?.object === "bank_account"
        ) {
          // this means we found an ACH payment method.
          doAnyAchPaymentMethodsExist = true;
        }
      });

    return doAnyAchPaymentMethodsExist || doAnyAchTransactionsExist; // if either is true then, an ACH transaction or method exists and we can't add another one.
  },

  processAuthorizationDocuments: (
    documents: UnFormattedDocument[],
    iocDocuments?: PatientDocument[]
  ) => {
    /**
     * determine the patient document status. if any document has a status that is not 'sent', then we are pending status.
     * We will take the statuses and boil them down to either 'pending' or 'signed'. i.e. if the status is not pending, then it is signed.
     */
    let iocDocumentStatus = "";
    let doIocDocumentsExist = false;
    if (iocDocuments && iocDocuments?.length > 0) {
      doIocDocumentsExist = true;
      iocDocuments?.forEach((document: PatientDocument) => {
        if (
          document?.signatureTracking?.signatureStatus !==
          SignatureStatus.SignatureCompleted
        ) {
          iocDocumentStatus = "pending";
        }
      });
      if (iocDocumentStatus !== "pending") {
        iocDocumentStatus = "signed";
      }
    }

    const sortedDocuments = {
      overallDocumentStatus: "signed",
      phiConsentDocuments: [],
      spaDocuments: [],
      pfrAdjustmentDocuments: [],
      installmentDocuments: []
    } as SortedDocuments;

    // Signed documents will come first, then unsigned.
    const dateSortedDocuments = _.orderBy(
      documents,
      [
        (document: UnFormattedDocument) =>
          document?.documentStatus === "signed" ? 1 : 0,
        (document: UnFormattedDocument) =>
          document?.authorizationDocumentStatusId ?? 0,
      ],
      ["desc", "desc"]
    );

    // loop through all documents. sort into the 3 types: PHI, SPA and PFR
    dateSortedDocuments.forEach((document: UnFormattedDocument) => {
      if (document?.authorizationDocuments?.typeOfDoc === "phi") {
        sortedDocuments.phiConsentDocuments.push({
          ...document,
          displayedDocumentStatus: Utils.getDocumentDisplayStatus(
            document.documentStatus || undefined
          ),
        });
      } else if (document?.authorizationDocuments?.typeOfDoc === "spa") {
        sortedDocuments.spaDocuments.push(
          // full pay docs
          {
            ...document,
            displayedDocumentStatus: Utils.getDocumentDisplayStatus(
              document.documentStatus || undefined
            ),
          }
        );
      } else if (document?.authorizationDocuments?.typeOfDoc === "pfr") {
        sortedDocuments.pfrAdjustmentDocuments.push({
          ...document,
          displayedDocumentStatus: Utils.getDocumentDisplayStatus(
            document.documentStatus || undefined
          ),
        });
      } else if (document?.authorizationDocuments?.typeOfDoc === "inst") {
        sortedDocuments.installmentDocuments.push({
          ...document,
          displayedDocumentStatus: Utils.getDocumentDisplayStatus(
              document.documentStatus || undefined
          ),
        });
      }
    });

    /**
     * Determine overall document status.
     *
     * 1. IOC doc exists and Authorization Documents exist.
     *   a. If one is Pending then the status will be Pending.
     *   b. if they are both Success then the status will be Success.
     * 2. IOC doc does NOT exist and Authorization Docs exist.
     *   a. If Authorization Docs are Pending then overall status is Pending.
     *   b. If Authorization Docs status is Success then overall status is Success.
     * 3. IOC doc exists and Authorization Docs do NOT exist.
     *   a. If  IOC docs are Pending then overall status is Pending.
     *   b. If  IOC docs status is Success then overall status is Success.
     * 4. Both don't exist.
     *   a. status will be No Documents.
     */

    sortedDocuments.doPfrAdjustmentDocumentsExist = !!(
      sortedDocuments?.pfrAdjustmentDocuments &&
      sortedDocuments?.pfrAdjustmentDocuments?.length > 0
    );
    sortedDocuments.doPhiConsentDocumentsExist = !!(
      sortedDocuments?.phiConsentDocuments &&
      sortedDocuments?.phiConsentDocuments?.length > 0
    );
    sortedDocuments.doFullPayDocumentsExist = !!(
      sortedDocuments?.spaDocuments && sortedDocuments?.spaDocuments?.length > 0
    );
    sortedDocuments.doInstallmentDocumentsExist = !!(
        sortedDocuments?.installmentDocuments && sortedDocuments?.installmentDocuments?.length > 0
    );

    const recentDocumentsToCheckStatus = [];
    if (sortedDocuments.doPfrAdjustmentDocumentsExist) {
      recentDocumentsToCheckStatus.push(
        sortedDocuments.pfrAdjustmentDocuments[0]
      );
    }
    if (sortedDocuments.doFullPayDocumentsExist) {
      recentDocumentsToCheckStatus.push(sortedDocuments.spaDocuments[0]);
    }
    if (sortedDocuments.doPhiConsentDocumentsExist) {
      recentDocumentsToCheckStatus.push(sortedDocuments.phiConsentDocuments[0]);
    }
    if (sortedDocuments.doInstallmentDocumentsExist) {
      recentDocumentsToCheckStatus.push(sortedDocuments.installmentDocuments[0]);
    }
    // If *every* existing document is signed then it's signed. if any document is pending then it's pending.

    let isEveryDocumentSigned = true;
    let isAnyDocumentPending = false;
    if (recentDocumentsToCheckStatus?.length > 0) {
      recentDocumentsToCheckStatus.forEach((document: FormattedDocument) => {
        if (document?.documentStatus !== "signed") {
          isEveryDocumentSigned = false;
        }
        if (
          document?.documentStatus === "sent" ||
          document?.documentStatus === "created"
        ) {
          isAnyDocumentPending = true;
        }
      });
    } else if (!doIocDocumentsExist) {
      sortedDocuments.overallDocumentStatus = "No Documents";
    }
    if (sortedDocuments.overallDocumentStatus !== "No Documents") {
      if (isEveryDocumentSigned) {
        if (doIocDocumentsExist && iocDocumentStatus === "pending") {
          sortedDocuments.overallDocumentStatus = "Pending Signature";
        } else {
          sortedDocuments.overallDocumentStatus = "Signature Completed";
        }
      } else if (
        isAnyDocumentPending ||
        (doIocDocumentsExist && iocDocumentStatus === "pending")
      ) {
        sortedDocuments.overallDocumentStatus = "Pending Signature";
      }
    }


    return sortedDocuments;
  },

  calculateReadjustedPfr: (
    initialPfrAmt: number,
    pfrAdjustments: PFRAdjustment[]
  ) => {
    let newPfrAmt: any = initialPfrAmt;
    if (
      newPfrAmt &&
      typeof newPfrAmt === "number" &&
      pfrAdjustments?.length > 0 &&
      pfrAdjustments?.length > 0 &&
      initialPfrAmt === pfrAdjustments[0]?.preAdjustmentPfrAmt
    ) {
      pfrAdjustments?.forEach(
        (pfrAdjustment: PFRAdjustment) =>
          (newPfrAmt = newPfrAmt + pfrAdjustment.adjustmentAmt)
      );
    } else {
      newPfrAmt = undefined; // pfr has already been adjusted or does not exist. this is here to prevent accidentally re-calculating the pfr twice.
    }
    return newPfrAmt;
  },

  // calculate the actual remaining balance (this is different from the pending remaining balance)
  calculateRemainingBalance: (
    groupedTransactions: Transaction[],
    pfrAmt: number | undefined
  ) => {
    let remainingBalanceSum: number = pfrAmt || 0;

    if (groupedTransactions.length > 0) {
      groupedTransactions.forEach((transaction: Transaction) => {
        if (
          transaction?.payment?.isCaptured &&
          transaction?.payment?.paymentStatusSuccess
        ) {
          // remove from running balance
          remainingBalanceSum -= transaction?.payment?.paymentAmt;
        }
        if (
          transaction?.paymentReversal?.reversalAmount &&
          transaction?.payment?.isCaptured &&
          transaction?.payment?.paymentStatusSuccess
        ) {
          // add to running balance
          remainingBalanceSum += Math.abs(
            transaction?.paymentReversal?.reversalAmount
          );
        }
        if (transaction?.paymentDispute?.disputeStatus === "lost") {
          remainingBalanceSum -= transaction?.paymentDispute?.disputeAmt;
        }
      });
    }
    return remainingBalanceSum;
  },


  robustErrorHandler: (
    error: {
      entity: { code: string; message: string };
      errorMessage: string;
      hasErrors: boolean;
    },
    thunkAPI: any
  ) => {
    const { entity, errorMessage } = error;
    if (
      (entity?.code !== "INTERNAL_SERVER_ERROR" && entity?.message) ||
      entity?.message
    ) {
      // handle external error
      thunkAPI.dispatch(showErrorStatus(entity?.message));
      throw new Error(entity?.message);
    } else {
      // handle internal error
      thunkAPI.dispatch(showErrorStatus(errorMessage));
      throw new Error(errorMessage);
    }
  },

};
export class PatientService {

  async getIOCCards(data: {
    offset: number;
    limit: number;
    dateRange: string;
    isConverted: boolean;
  }): Promise<AxiosResultStatus> {
    const { offset, limit, dateRange, isConverted } = data;

    let range: string;
    if (dateRange !== "3") {
      // 6, 12, or 0 (zero means all)
      range = `&range=${dateRange}`;
    } else {
      range = ""; // no range in querystring. Defaults to 3 (months)
    }

    const readPayload: AxiosReadPayload = {
      dataId: 0,
      url: `patient-encounter/v2/patient/encounter?fetchIOCCards=true&offset=${offset}&limit=${limit}&isConverted=${isConverted}${range}`,
    };
    return await axiosReadHelper(readPayload);
  }

  /** In-flight getPatient requests by patientId so duplicate calls reuse the same request */
  private static getPatientPromiseByPatientId = new Map<number, Promise<AxiosResultStatus>>();

  async getPatient(patientId: number): Promise<AxiosResultStatus> {
    const existing = PatientService.getPatientPromiseByPatientId.get(patientId);
    if (existing) return existing;
    const readPayload: AxiosReadPayload = {
      dataId: patientId,
      url: "patient/v2/patient",
    };
    const promise = axiosReadHelper(readPayload).finally(() => {
      PatientService.getPatientPromiseByPatientId.delete(patientId);
    });
    PatientService.getPatientPromiseByPatientId.set(patientId, promise);
    return promise;
  }


  //update method name once above one is deprecated
  async updatePatient(
    data: any,
    patientId: number,
    cancelSignal?: AbortSignal
  ): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: data,
      dataId: patientId,
      url: "patient/v2/patient",
      signal: cancelSignal,
    };
    return await axiosSaveHelper(payload);
  }


  async PaymentReceipt( paymentId: number, receiptEmail?: string | undefined ): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: receiptEmail ? { receiptEmail } : {},
      dataId:0,
      isPatch: false,
      url: `payment/v2/payment/${paymentId}/receipt`,
    }
  
    return await axiosSaveHelper(payload);
  }

  async resendRefundReceipt(
    receiptEmail: string,
    paymentReversalId: number
): Promise<AxiosResultStatus>{
  const payload: AxiosSavePayload = {
    dataToSave: {receiptEmail},
    dataId: 0,
    isPatch: false,
    url:`payment/v2/refund/${paymentReversalId}/receipt`
};
return await axiosSaveHelper(payload)
}

  async resendDisputeReceipt(receiptEmail: string, paymentId: number): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: {receiptEmail},
      dataId: 0,
      isPatch: false,
      url:`payment/v2/dispute/${paymentId}/receipt`
    };
    return await axiosSaveHelper(payload)
  }

  async updateInstanceOfCare(
    patientEncounterId: number
  ): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: {
        vobDate: new Date().toISOString(),
        patientEncounterId: patientEncounterId,
      },
      dataId: -2, // less than 0 is post...
      isPatch: true, // but is patch true makes it a put... yikes
      url: `patient-encounter/v2/patient/encounter/${patientEncounterId}`,
    };
    const saveResult = await axiosSaveHelper(payload);

    if (!saveResult.hasErrors) {
      saveResult.entity = this.mapToInstanceOfCareOverview(saveResult.entity);
    }
    return saveResult;
  }

  // pass any individual IOC fields under 'data'; encounterId is required.
  async saveIOCWithPartialObject(
    data: any,
    encounterId: number
  ): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: { ...data },
      dataId: -2,
      isPatch: true,
      url: `patient-encounter/v2/patient/encounter/${encounterId}`,
    };
    return await axiosSaveHelper(payload);
  }

  async updatePatientChampion(
    champion: any,
    championId: number,
    cancelSignal?: AbortSignal
  ): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: _.omit(champion, ["patientChampionId"]), //omitting pk just in case
      dataId: championId,
      url: `patient-encounter/v2/patient/encounter/champion/${championId}`,
      isPatch: true,
      signal: cancelSignal,
    };
    return await axiosSaveHelper(payload);
  }

  async createCustomer(
    customer: any,
    cancelSignal?: AbortSignal
  ): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: customer, //omitting pk just in case
      url: `payment/v2/customer`,
      signal: cancelSignal,
      dataId: 0,
    };
    return await axiosSaveHelper(payload);
  }

  async createPatientChampion(
    champion: any,
    cancelSignal?: AbortSignal
  ): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: champion, //omitting pk just in case
      url: `patient-encounter/v2/patient/encounter/champion`,
      signal: cancelSignal,
      dataId: 0,
    };
    return await axiosSaveHelper(payload);
  }

  async savePatientPaymentProgram(data: {
    paymentProgram: PatientPaymentProgram;
    patientId: number;
    encounterId: number;
  }): Promise<AxiosResultStatus> {
    const { paymentProgram, patientId, encounterId } = data;
    paymentProgram.patientId = patientId;
    paymentProgram.patientEncounterId = encounterId;
    const payload: AxiosSavePayload = {
      dataToSave: {
        ...paymentProgram,
        patientPaymentSchedule: {
          ...paymentProgram.patientPaymentSchedule,
          pfrBalance: undefined,
          pfrAmt: paymentProgram.patientPaymentSchedule.pfrAmt || 0,
        }
      },
      dataId: paymentProgram.patientPaymentProgramId,
      url: `payment/v2/program`,
    };
    return await axiosSaveHelper(payload);
  }

  // async getDocuments(
  //   patientId: number,
  //   encounterId: number
  // ): Promise<AxiosResultStatus> {
  //   const payload: AxiosReadPayload = {
  //     dataId: 0,
  //     url: `patientrecord/patient/${patientId}/encounter/${encounterId}/document/listing`,
  //   };
  //   return await axiosReadHelper(payload);
  // }

  async getLedger(
    patientId?: number,
    encounterId?: number,
  ): Promise<AxiosResultStatus> {
    let url = `payment/v2/payment/ledger?`

    if (encounterId) {
      url += `encounterId=${encounterId}`
    } else if (patientId) {
      url += `patientId=${patientId}`
    }

    url += `&isClosed=false`

    const payload: AxiosReadPayload = {
      dataId: 0,
      url: url,
    };

    return await axiosReadHelper(payload);
  }
  
  async getPaymentDetails(
    ledgerId: number
  ): Promise<any> {
    let url = `payment/v2/payment?ledgerId=${ledgerId}`

    const payload: AxiosReadPayload = {
      dataId: 0,
      url: url,
    };

    try {
      const result = await axiosReadHelper(payload);
      if (result.hasErrors) {
        console.error(`Error from payment details API: ${result.errorMessage}`);
        return null;
      }
      return result.entity;
    } catch (error) {
      console.error(`Exception calling payment details API: ${error}`);
      return null;
    }
  }

  async getPfrDetails(
    ledgerId: number
  ): Promise<any> {
    let url = `payment/v2/pfradjustment?ledgerId=${ledgerId}`

    const payload: AxiosReadPayload = {
      dataId: 0,
      url: url,
    };

    try {
      const result = await axiosReadHelper(payload);
      if (result.hasErrors) {
        console.error(`Error from PFR details API: ${result.errorMessage}`);
        return null;
      }
      return result.entity;
    } catch (error) {
      console.error(`Exception calling PFR details API: ${error}`);
      return null;
    }
  }

  async getDisputeDetails(
    ledgerId: number
  ): Promise<any> {
    let url = `payment/v2/dispute?ledgerId=${ledgerId}`

    const payload: AxiosReadPayload = {
      dataId: 0,
      url: url,
    };

    try {
      const result = await axiosReadHelper(payload);
      if (result.hasErrors) {
        console.error(`Error from dispute details API: ${result.errorMessage}`);
        return null;
      }
      return result.entity;
    } catch (error) {
      console.error(`Exception calling dispute details API: ${error}`);
      return null;
    }
  }

  async getReversalDetails(
    ledgerId: number
  ): Promise<any> {
    let url = `payment/v2/reversal?ledgerId=${ledgerId}`

    const payload: AxiosReadPayload = {
      dataId: 0,
      url: url,
    };

    try {
      const result = await axiosReadHelper(payload);
      if (result.hasErrors) {
        console.error(`Error from reversal details API: ${result.errorMessage}`);
        return null;
      }
      return result.entity;
    } catch (error) {
      console.error(`Exception calling reversal details API: ${error}`);
      return null;
    }
  }

  async getPaymentMethods(
    patientId: number,
    encounterId: number
  ): Promise<AxiosResultStatus> {
    const payload: AxiosReadPayload = {
      dataId: 0,
      url: `patientrecord/patient/${patientId}/encounter/${encounterId}/paymentmethod/listing`,
    };

    return await axiosReadHelper(payload);
  }

  async getContactInfo(
    patientId: number,
    encounterId: number
  ): Promise<AxiosResultStatus> {
    const payload: AxiosReadPayload = {
      dataId: 0,
      url: `patient-encounter/v2/encounter/${encounterId}/contact`,
    };

    return await axiosReadHelper(payload);
  }

  async updateContactInfo(
    patientId: number,
    encounterId: number,
    patientContactInfo: patientEncounterContact
  ): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: patientContactInfo,
      dataId: encounterId,
      isPatch: true,
      url: `patient-encounter/v2/encounter/${encounterId}/contact`,
    };

    return await axiosSaveHelper(payload);
  }

  async addPatientPaymentMethod(
    patientId: number,
    encounterId: number,
    paymentDetail: PaymentDetail
  ): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: paymentDetail,
      dataId: 0,
      url: `patientrecord/patient/${patientId}/encounter/${encounterId}/paymentmethod`,
    };
    return await axiosSaveHelper(payload);
  }

  async deletePatientPaymentMethod(
    patientId: number,
    encounterId: number,
    stripeId: string,
    paymentTypeId: number
  ): Promise<AxiosResultDeleteStatus> {
    const deletePayload: AxiosDeletePayload = {
      dataId: paymentTypeId,
      url: `patientrecord/patient/${patientId}/encounter/${encounterId}/paymentmethod/${stripeId}/paymentmethodtype`,
    };
    return await axiosDeleteHelper(deletePayload);
  }

  async verifyBankAccount(
    patientId: number,
    encounterId: number,
    microdeposits: ACHVerification
  ): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: microdeposits,
      dataId: 0,
      url: `patientrecord/patient/${patientId}/encounter/${encounterId}/ach-verification`,
    };
    return await axiosSaveHelper(payload);
  }

  async makePayment(payment: Payment): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: payment,
      dataId: 0,
      url: `patientrecord/patient/${payment.patientId}/encounter/${payment.patientEncounterId}/payment`,
    };
    return await axiosSaveHelper(payload);
  }

  async createPayment(payment: Payment): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: payment,
      dataId: 0,
      url: `payment/v2/payment`,
    };
    return await axiosSaveHelper(payload);
  }

  async listPaymentMethodsByContactId(contactId: number): Promise<AxiosResultStatus> {
    const payload: AxiosReadPayload = {
      dataId: 0,
      url: `payment/v2/contacts/${contactId}/paymentmethods`,
    };
    return await axiosReadHelper(payload);
  }

  async releaseCharge(
    payment: V2PaymentDetail | Payment
  ): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = { // use in local to test the post
      dataToSave: payment,
      dataId: 0,
      url: `payment/v2/payment/${payment.paymentId}/refund`,
    };
    return await axiosSaveHelper(payload);
  }

  /** In-flight getPatientInstanceOfCare by patientId-encounterId so duplicate calls reuse the same request */
  private static getPatientInstanceOfCarePromiseByKey = new Map<string, Promise<AxiosResultStatus>>();

  async getPatientInstanceOfCare(
    data: { patientId: number; encounterId: number | string },
    cancelSignal?: AbortSignal
  ): Promise<AxiosResultStatus> {
    const key = `${data.patientId}-${data.encounterId}`;
    const existing = PatientService.getPatientInstanceOfCarePromiseByKey.get(key);
    if (existing) return existing;
    const payload: AxiosReadPayload = {
      dataId: data.encounterId,
      url: `patient-encounter/v2/patient/encounter`,
      signal: cancelSignal,
    };
    const promise = axiosReadHelper(payload).finally(() => {
      PatientService.getPatientInstanceOfCarePromiseByKey.delete(key);
    });
    PatientService.getPatientInstanceOfCarePromiseByKey.set(key, promise);
    return promise;
  }

  async getPatientEncounterSignUrl(data: {
    patientId: number;
    encounterId: number;
  }): Promise<AxiosResultStatus> {
    const payload: AxiosReadPayload = {
      url: `patientrecord/patient/${data.patientId}/encounter/${data.encounterId}/signingurl`,
    };
    return await axiosReadHelper(payload);
  }

  async saveStatus(data: {
    workFlow: Workflow | Omit<Workflow, "workflowName">;
    encounterId: number;
    patientId: number;
  }) {
    const { workFlow, encounterId } = data;
    const payload: AxiosSavePayload = {
      dataToSave: workFlow,
      dataId: encounterId,
      isPatch: true,
      url: `patient-encounter/v2/patient/encounter/${encounterId}/status`,
    };
    return await axiosSaveHelper(payload);
  }

  async downloadHelloSignDocument(
    patientId: number,
    encounterId: number,
    externalDocumentId: any
  ): Promise<AxiosResultStatus> {
    const readPayload: AxiosReadPayload = {
      url: `patientrecord/patient/${patientId}/encounter/${encounterId}/signdocument/${externalDocumentId}/download`,
      customHeader: {
        Accept: "application/pdf",
        "Content-Type": "application/pdf",
      },
    };
    return await axiosDownloadHelper(readPayload);
  }

  async helloSignReminder(patientEncounter: PatientEncounter) {
    const { patientId, patientEncounterId } = patientEncounter;
    const { patientDocumentId } = patientEncounter.patientDocument?.[0];

    const payload: AxiosSavePayload = {
      dataToSave: {}, // no payload
      dataId: 0, // 0 means POST
      url: `patientrecord/patient/${patientId}/encounter/${patientEncounterId}/document/${patientDocumentId}/remind`,
    };
    return await axiosSaveHelper(payload);
  }

  async helloSignForward(patientEncounter: PatientEncounter, email: string) {
    const { patientId, patientEncounterId } = patientEncounter;
    const { patientDocumentId } = patientEncounter.patientDocument?.[0];

    const payload: AxiosSavePayload = {
      dataToSave: { email: email },
      dataId: 0, // 0 means POST
      url: `patientrecord/patient/${patientId}/encounter/${patientEncounterId}/document/${patientDocumentId}/forward`,
    };
    return await axiosSaveHelper(payload);
  }

  //need to ask what the purpose of this mapping function is
  mapToInstanceOfCareOverview(instanceOfCare: InstanceOfCareViewModel) {
    return {
      patientEncounterId: instanceOfCare.patientEncounterId,
      patientId: instanceOfCare.patientId,
      clientId: instanceOfCare.clientId,
      clientName: instanceOfCare.clientName,
      facilityId: instanceOfCare.facilityId,
      facilityName: instanceOfCare.facilityName,
      hasChampions: instanceOfCare.hasChampions,
      clientsPatientAccountId: instanceOfCare.clientsPatientAccountId,
      clientsPatientIOCId: instanceOfCare.clientsPatientIOCId,
      clientBillingIOCId: instanceOfCare.clientBillingIOCId,
      instanceCreateDt: instanceOfCare.instanceCreateDt,
      pfrAmt: instanceOfCare.pfrAmt,
      levelOfCare: instanceOfCare.levelOfCare,
      timingRisk: instanceOfCare.timingRisk,
      payorRisk: instanceOfCare.payorRisk,
      riskClass: instanceOfCare.riskClass,
      typeOfCare: instanceOfCare.typeOfCare,
      admissionDt: instanceOfCare.admissionDt,
      dischargeDt: instanceOfCare.dischargeDt,
      paidToPatient: instanceOfCare.paidToPatient,
      client: instanceOfCare.client,
      workflow: instanceOfCare.workflow,
      //need to include autoCalcenabled, isRiskClassOverriden, mceSent, romVersion, vobDate?
    } as InstanceOfCareViewModel;
  }

  async uploadS3File(
    patientId: string,
    encounterId: string,
    file: any
  ): Promise<AxiosResultStatus> {
    let data = new FormData();
    data.append("file", file);
    const payload: AxiosSavePayload = {
      dataToSave: data,
      dataId: 0,
      url: `patientrecord/patient/${patientId}/encounter/${encounterId}/document`,
    };

    return await axiosSaveHelper(payload);
  }

  async getS3Documents(
    patientId: number,
    encounterId: number
  ): Promise<AxiosResultStatus> {
    const payload: AxiosReadPayload = {
      dataId: 0,
      url: `patient-encounter/v2/patient/${patientId}/encounter/${encounterId}/s3document`,
    };
    return await axiosReadHelper(payload);
  }

  async deleteS3Document(
    patientId: string,
    encounterId: string,
    s3Document: string
  ): Promise<AxiosResultStatus> {
    const readPayload: AxiosReadPayload = {
      dataId: 0,
      url: `patientrecord/patient/${patientId}/encounter/${encounterId}/s3document/delete/${s3Document}`,
    };
    return await axiosReadHelper(readPayload);
  }

  async downloads3Document(
    patientId: string,
    encounterId: string,
    s3Document: string
  ): Promise<AxiosResultStatus> {
    const readPayload: AxiosReadPayload = {
      url: `patient-encounter/v2/patient/${patientId}/encounter/${encounterId}/s3document/${encodeURIComponent(s3Document)}`,
    };

    if (s3Document.split(".").pop() === "pdf") {
      readPayload.customHeader = {
        Accept: "application/pdf",
        "Content-Type": "application/pdf",
      };
      readPayload.responseType = 'arraybuffer'
    }

    const responseData = await axiosDownloadFileHelper(readPayload);

    const url = window.URL.createObjectURL(
      new Blob([responseData.entity.data], {
        type: responseData.entity.headers["content-type"],
      })
    );

    const link = document.createElement("a");
    link.href = url;
    link.download = s3Document;
    document.body.appendChild(link);
    link.click();
    link.remove();

    responseData.entity = null;

    return responseData;
  }

  async updateDocumentSignature(data: {
    data: any,
    encounterId: number
  }): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: data.data,
      dataId: -2,
      isPatch: true,
      url: `patient-encounter/v2/patient/encounter/${data.encounterId}/document`,
    };
    return await axiosSaveHelper(payload);
  }


  async integrationUpdate(data: {
    patientEncounterId: number;
    patientId: number;
    crmTypeSlug?: string;
  }): Promise<any> {
    const instanceOfCare = await this.getPatientInstanceOfCare({
      patientId: data.patientId,
      encounterId: data.patientEncounterId,
    });

    const payload: AxiosSavePayload = {
      dataToSave: instanceOfCare.entity,
      dataId: 0,
      url: `${data.crmTypeSlug}/event/finpay`,
    };
    if(instanceOfCare?.entity?.clientsPatientIOCId || !data.crmTypeSlug) {
      return await axiosSaveHelper(payload);
    }
  }




  async updateAuthorizationDocumentStatus(
      data: {
        dataToSave: {
          documentStatus?: string,
          externalSignatureRequestId?: string,
          wasPfrAdjustmentSent?: boolean,
          dateApproved?: string | null
        },
        authorizationDocumentStatusId: number
      }
  ): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: data.dataToSave,
      dataId: data.authorizationDocumentStatusId,
      isPatch: false,
      url: `patient-encounter/v2/patient/encounter/authorizationDocumentStatus`,
    }
    return await axiosSaveHelper(payload);
  }

  async createNoSigHelloSignDoc(
      data: {
        authorizationDocumentStatusId: number
      }
  ): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataId: 0,
      isPatch: false,
      url: `patient-encounter/v2/patient/encounter/authorizationDocumentStatus/${data.authorizationDocumentStatusId}/noSigDocument`,
    }
    return await axiosSaveHelper(payload);
  }

  getAuthDocQueryStringParam(dispatchMethod?: authDocDispatchMethod){

    let queryStringParam;

    switch(dispatchMethod){
      case authDocDispatchMethod.email:
        queryStringParam = '?sendEmail=true'
        break;
      case authDocDispatchMethod.sms:
        queryStringParam = '?sendSms=true'
        break;
      default:
        queryStringParam = ""
    }

    return queryStringParam
  }

  async spaDocument(
      encounterId: number,
      dispatchMethod?: authDocDispatchMethod): Promise<AxiosResultStatus> {

    const payload: AxiosSavePayload = {
      dataToSave: {},
      dataId: 0,
      url: `patient-encounter/v2/patient/encounter/${encounterId}/authDocs/spaDocument${this.getAuthDocQueryStringParam(dispatchMethod)}`,
    };
    return await axiosSaveHelper(payload);
  }

  async installmentDocument(
      encounterId: number,
      dispatchMethod?: authDocDispatchMethod): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: {},
      dataId: 0,
      url: `patient-encounter/v2/patient/encounter/${encounterId}/authDocs/installmentDocument${this.getAuthDocQueryStringParam(dispatchMethod)}`,
    };
    return await axiosSaveHelper(payload);
  }

  async createPaymentMethod(paymentMethod: PaymentMethod): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: paymentMethod,
      dataId: 0,
      url: `payment/v2/payment/payment-method`,
    };
    return await axiosSaveHelper(payload);
  }

  async createPaymentIntents(paymentIntents: PaymentIntents): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: paymentIntents,
      dataId: 0,
      url: `payments/v3/payment-intent`,
    };
    return await axiosSaveHelper(payload);
  }

  async getPayment(programId: number, paymentId: number): Promise<AxiosResultStatus> {
    const payload: AxiosReadPayload = {
      dataId: paymentId,
      url: `payments/v3/program/${programId}/payment`,
    };
    return await axiosReadHelper(payload);
  }

  async updatePaymentIntents(paymentIntentId: string, paymentIntents: PaymentIntents): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: paymentIntents,
      dataId: 1, // dataId > 0 with isPatch = true triggers PATCH request
      isPatch: true,
      url: `payments/v3/payment-intent/${paymentIntentId}`,
    };
    return await axiosSaveHelper(payload);
  }

  async legacyInstallmentDocument(paymentProgramId: number): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: {},
      dataId: 0,
      url: `payment/v2/program/${paymentProgramId}/document`,
    };
    return await axiosSaveHelper(payload);
  }

  async sendNotification(data: {
    username: string;
    templateId: number;
  }): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: data,
      dataId: 0,
      url: `patient-encounter/v2/notification`,
    };
    return await axiosSaveHelper(payload);
  }

  async getPatientDocuments(
      patientEncounterId: number,
      cancelSignal?: AbortSignal
  ): Promise<AxiosResultStatus> {

    const payload: AxiosReadPayload = {
      url: `patient-encounter/v2/patient/encounter/${patientEncounterId}/documents`,
      signal: cancelSignal,
    };

    return axiosReadHelper(payload)
  }


}

export const patientService = new PatientService();
